import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnDanger, BtnGhost,
  Modal, Field, Input, Select, Textarea, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, DownloadSimple, PencilSimple, Trash, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import ProjectSelect from "@/components/ProjectSelect";
import PhotoUploader, { PhotoThumb } from "@/components/PhotoUploader";

const BLANK = {
  sku: "", name: "", category: "General", unit: "ea",
  unit_cost: 0, quantity: 0, reorder_level: 0,
  location: "Main Warehouse", supplier: "", notes: "", photo_url: null,
};

export default function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjItem, setAdjItem] = useState(null);
  const [adjForm, setAdjForm] = useState({ delta: 0, reason: "", project_code: "" });
  const canEdit = can(user.role, "staff");
  const canDelete = can(user.role, "manager");

  async function load() {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (lowOnly) params.set("low_only", "true");
      const { data } = await api.get(`/inventory?${params}`);
      setItems(data);
    } catch (e) { toast.error(formatError(e)); }
  }

  useEffect(() => { load(); }, [q, lowOnly]);

  function openCreate() {
    setEditing(null); setForm(BLANK); setEditOpen(true);
  }
  function openEdit(i) {
    setEditing(i); setForm({ ...BLANK, ...i }); setEditOpen(true);
  }
  async function save(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        unit_cost: Number(form.unit_cost),
        quantity: Number(form.quantity),
        reorder_level: Number(form.reorder_level),
      };
      if (editing) await api.put(`/inventory/${editing.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success(editing ? "Item updated" : "Item created");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(i) {
    if (!window.confirm(`Delete ${i.sku}?`)) return;
    try {
      await api.delete(`/inventory/${i.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatError(e)); }
  }
  function openAdjust(i) {
    setAdjItem(i); setAdjForm({ delta: 0, reason: "", project_code: "" }); setAdjOpen(true);
  }
  async function adjust(e) {
    e.preventDefault();
    try {
      await api.post(`/inventory/${adjItem.id}/adjust`, {
        delta: Number(adjForm.delta), reason: adjForm.reason,
        project_code: adjForm.project_code || null,
      });
      toast.success("Stock adjusted");
      setAdjOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="inventory-page">
      <PageHeader
        overline="Module / Inventory"
        title="Stock"
        subtitle="Manually tracked SKUs with reorder levels and movement audit log."
        actions={
          <>
            <BtnSecondary
              data-testid="inv-export-csv"
              onClick={() => exportCSV(items, `inventory-${Date.now()}.csv`)}
            >
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> Export CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary data-testid="inv-add-button" onClick={openCreate}>
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Add Item
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card p-4 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-[#a1a1aa]" />
          <input
            data-testid="inv-search"
            placeholder="Search SKU, name, category…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-[#e4e4e7] bg-white pl-9 pr-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#52525b]">
          <input
            data-testid="inv-low-only"
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => setLowOnly(e.target.checked)}
          /> Low stock only
        </label>
      </div>

      <div className="phizo-card">
        {items.length === 0 ? (
          <div className="p-5"><Empty label="No inventory matches." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>SKU</th><th>Name</th><th>Category</th><th>Location</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Reorder</th>
                  <th className="text-right">Unit Cost</th>
                  <th className="text-right">Value</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const low = i.quantity <= i.reorder_level;
                  return (
                    <tr key={i.id} data-testid={`inv-row-${i.sku}`}>
                      <td className="mono text-xs">{i.sku}</td>
                      <td className="font-medium">
                        {i.name}
                        {low && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-[#FF2A2A]">
                            <Warning size={12} weight="fill" /> LOW
                          </span>
                        )}
                      </td>
                      <td className="text-sm text-[#52525b]">{i.category}</td>
                      <td className="text-sm text-[#52525b]">{i.location}</td>
                      <td className={`text-right mono ${low ? "text-[#FF2A2A] font-bold" : ""}`}>{i.quantity}</td>
                      <td className="text-right mono text-[#52525b]">{i.reorder_level}</td>
                      <td className="text-right mono">{fmtZMW(i.unit_cost)}</td>
                      <td className="text-right mono font-semibold">{fmtZMW(i.quantity * i.unit_cost)}</td>
                      <td className="text-right whitespace-nowrap">
                        {canEdit && (
                          <BtnGhost
                            data-testid={`inv-adjust-${i.sku}`}
                            onClick={() => openAdjust(i)}
                          >
                            <ArrowsClockwise size={12} weight="bold" /> Adjust
                          </BtnGhost>
                        )}
                        {canEdit && (
                          <BtnGhost onClick={() => openEdit(i)} data-testid={`inv-edit-${i.sku}`}>
                            <PencilSimple size={12} weight="bold" />
                          </BtnGhost>
                        )}
                        {canDelete && (
                          <BtnGhost
                            onClick={() => remove(i)}
                            className="text-[#FF2A2A]"
                            data-testid={`inv-delete-${i.sku}`}
                          >
                            <Trash size={12} weight="bold" />
                          </BtnGhost>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit modal */}
      <Modal
        open={editOpen} onClose={() => setEditOpen(false)}
        title={editing ? `Edit ${editing.sku}` : "New Inventory Item"}
        testId="inv-edit-modal"
      >
        <form onSubmit={save} className="space-y-4">
          <div className="border border-[#e4e4e7] p-4 bg-[#fafafa]">
            <div className="phizo-label mb-2">Item Photo</div>
            <PhotoUploader
              value={form.photo_url}
              onChange={(url) => setForm({ ...form, photo_url: url })}
              category="inventory"
              size={88}
              testId="inv-photo"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SKU">
              <Input
                required data-testid="inv-form-sku"
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                disabled={!!editing}
              />
            </Field>
            <Field label="Category">
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Name">
            <Input
              required data-testid="inv-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Unit">
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </Field>
            <Field label="Quantity">
              <Input type="number" step="0.01"
                data-testid="inv-form-qty"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </Field>
            <Field label="Reorder Level">
              <Input type="number" step="0.01"
                value={form.reorder_level}
                onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Unit Cost (ZMW)">
              <Input type="number" step="0.01"
                value={form.unit_cost}
                onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
            </Field>
            <Field label="Location">
              <Input value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </Field>
          </div>
          <Field label="Supplier">
            <Input value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          </Field>
          <Field label="Notes">
            <Textarea value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setEditOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="inv-form-save">{editing ? "Save" : "Create"}</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* Adjust modal */}
      <Modal open={adjOpen} onClose={() => setAdjOpen(false)}
        title={`Adjust: ${adjItem?.name || ""}`} testId="inv-adjust-modal">
        <form onSubmit={adjust} className="space-y-4">
          <div className="phizo-surface p-3 flex items-center justify-between">
            <div>
              <div className="phizo-label">Current</div>
              <div className="font-heading text-2xl font-black mt-1">{adjItem?.quantity} {adjItem?.unit}</div>
            </div>
            <div className="text-right">
              <div className="phizo-label">Reorder at</div>
              <div className="mono font-bold text-[#52525b] mt-1">{adjItem?.reorder_level}</div>
            </div>
          </div>
          <Field label="Delta (+ in, − out)">
            <Input type="number" step="0.01" required
              data-testid="adj-delta"
              value={adjForm.delta}
              onChange={(e) => setAdjForm({ ...adjForm, delta: e.target.value })} />
          </Field>
          <Field label="Reason">
            <Input required
              data-testid="adj-reason"
              placeholder="e.g. issued to WO-0004"
              value={adjForm.reason}
              onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-2 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setAdjOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="adj-save">Apply</BtnPrimary>
          </div>
        </form>
      </Modal>
    </div>
  );
}
